/**
 * Injected into <head> by `prepareThumbnailHtml` so the off-screen capture
 * iframe always lays out at a desktop viewport regardless of what the
 * source page's viewport meta says.
 *
 * The viewport meta alone forces `vw` units, media queries, and any
 * JS-style `window.innerWidth` reads to resolve at 1280px — enough to
 * trigger desktop-style layout without aggressively overriding body
 * width. We previously also injected an `html,body{width:1280px}` reset,
 * but that broke agent-generated pages whose body uses `display: flex` /
 * `display: grid` to center content, producing black/empty captures.
 */
const VIEWPORT_INJECTION = `<meta name="viewport" content="width=1280, initial-scale=1">`;

/**
 * Sanitize HTML before injecting it into the capture iframe.
 *
 * Two responsibilities:
 *
 *   1. Strip <script> tags (the sandbox blocks execution but the browser
 *      still logs noisy "Blocked script execution" warnings) and
 *      `autoplay` attributes on <video>/<audio>/<iframe> so a list of
 *      20 rows doesn't kick off playback in every off-screen iframe.
 *   2. Inject a desktop viewport meta (see `VIEWPORT_INJECTION`) so the
 *      iframe's layout viewport resolves at 1280px and the page renders
 *      its desktop branch (media queries, `vw` units, etc.). We do NOT
 *      force body width here — that breaks agent-generated pages that
 *      use `display: flex` / `display: grid` body layouts to center
 *      content and produces black/empty captures.
 *
 * NOTE: Intentionally does NOT inject any animation-killing CSS. The v3.0
 * implementation injected `*{animation:none!important;...}` here, which
 * meant the 1500 ms snapshot delay only ever captured the initial frame —
 * the bug v3.1 fixes (see DESIGN_V3_1.md §4). The new pipeline waits long
 * enough for animations to land on their final frame instead of freezing
 * them at frame zero.
 */
export function prepareThumbnailHtml(html: string): string {
  let out = html
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<script\b[^>]*\/?>/gi, '')
    .replace(/(<(?:video|audio|iframe)\b[^>]*?)\sautoplay(=(?:"[^"]*"|'[^']*'|[^\s>]+))?/gi, '$1');

  // Inject viewport meta + width reset into <head>. We append to an
  // existing <head> when present (so the source page's <title>, <link>,
  // etc. are preserved); otherwise wrap the doc with a synthetic <head>.
  // The injection lands immediately AFTER the opening <head> tag's `>`
  // so it overrides any author-supplied viewport meta that came earlier
  // (later meta tags win in browsers) while preserving any attributes
  // on the <head> tag itself (e.g. `<head lang="en">`).
  const headOpenRe = /<head\b[^>]*>/i;
  const headOpenMatch = out.match(headOpenRe);
  if (headOpenMatch) {
    out = out.replace(headOpenRe, `${headOpenMatch[0]}${VIEWPORT_INJECTION}`);
  } else if (/<html\b/i.test(out)) {
    out = out.replace(/<html\b[^>]*>/i, (match) => `${match}<head>${VIEWPORT_INJECTION}</head>`);
  } else {
    out = `<head>${VIEWPORT_INJECTION}</head>${out}`;
  }
  return out;
}
